import { useEffect, useState } from "react";
import { useCart } from "@/context/CartContext";
import { api, money } from "@/lib/api";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const [email, setEmail] = useState("");
  const [provider, setProvider] = useState("stripe");
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [loading, setLoading] = useState(false);

  const shipping = subtotal >= 50 ? 0 : subtotal === 0 ? 0 : 6.99;
  const total = Math.max(0, subtotal - discount + shipping);

  const applyCoupon = async () => {
    if (!coupon) return;
    try {
      const r = await api.post("/coupons/validate", { code: coupon, subtotal });
      setDiscount(r.data.discount);
      toast.success(`Coupon applied — ${r.data.description}`);
    } catch (e) {
      setDiscount(0);
      toast.error(e.response?.data?.detail || "Invalid coupon");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setLoading(true);
    try {
      const r = await api.post("/checkout", {
        items: items.map((i) => ({
          product_id: i.product_id,
          variant_sku: i.variant_sku,
          quantity: i.quantity,
        })),
        email,
        origin_url: window.location.origin,
        coupon_code: coupon || null,
        provider,
      });
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error("Could not start checkout.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 text-center fade-in" data-testid="checkout-empty">
        <SEO title="Checkout" path="/checkout" />
        <h1 className="text-h2 mb-4">Your cart is empty.</h1>
        <p className="text-[#86868B] mb-8">Head to the shop to add some gear.</p>
        <Link to="/shop" className="btn-primary inline-block">Shop CreatorMic Pro</Link>
      </div>
    );
  }

  return (
    <div className="fade-in" data-testid="page-checkout">
      <SEO title="Checkout" description="Secure checkout" path="/checkout" />
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16 grid lg:grid-cols-5 gap-10">
        <form onSubmit={submit} className="lg:col-span-3 space-y-8">
          <div>
            <p className="eyebrow mb-2">Checkout</p>
            <h1 className="text-h2 text-[#1D1D1F]">Almost there.</h1>
          </div>

          <div>
            <label className="text-sm font-medium text-[#1D1D1F]">Email address</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="checkout-email"
              placeholder="you@creator.com"
              className="mt-2 w-full h-12 px-4 rounded-xl bg-[#F5F5F7] border border-transparent focus:border-[#0066FF] focus:bg-white focus:ring-1 focus:ring-[#0066FF] focus:outline-none"
            />
            <p className="mt-2 text-xs text-[#86868B]">We will send your order confirmation and tracking updates here.</p>
          </div>

          <div>
            <label className="text-sm font-medium text-[#1D1D1F]">Payment method</label>
            <div className="mt-2 grid sm:grid-cols-2 gap-3">
              <label className={`p-4 rounded-2xl border-2 cursor-pointer ${provider === "stripe" ? "border-[#0066FF] bg-[#0066FF]/5" : "border-black/10"}`} data-testid="pay-stripe">
                <input type="radio" name="provider" value="stripe" checked={provider === "stripe"} onChange={() => setProvider("stripe")} className="sr-only" />
                <p className="font-semibold text-[#1D1D1F]">Credit / Debit</p>
                <p className="text-xs text-[#86868B] mt-1">Secure Stripe Checkout. Cards, Apple Pay, Google Pay.</p>
              </label>
              <label className={`p-4 rounded-2xl border-2 cursor-pointer ${provider === "paypal" ? "border-[#0066FF] bg-[#0066FF]/5" : "border-black/10"}`} data-testid="pay-paypal">
                <input type="radio" name="provider" value="paypal" checked={provider === "paypal"} onChange={() => setProvider("paypal")} className="sr-only" />
                <p className="font-semibold text-[#1D1D1F]">PayPal</p>
                <p className="text-xs text-[#86868B] mt-1">Log in and pay with your PayPal balance or linked cards.</p>
              </label>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-[#1D1D1F]">Discount code</label>
            <div className="mt-2 flex gap-2">
              <input
                value={coupon}
                onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                data-testid="checkout-coupon"
                placeholder="WELCOME10"
                className="flex-1 h-12 px-4 rounded-xl bg-[#F5F5F7] border border-transparent focus:border-[#0066FF] focus:bg-white focus:ring-1 focus:ring-[#0066FF] focus:outline-none"
              />
              <button type="button" onClick={applyCoupon} className="btn-secondary" data-testid="checkout-apply-coupon">Apply</button>
            </div>
          </div>

          <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={loading} data-testid="checkout-submit">
            <Lock size={14} /> {loading ? "Redirecting..." : `Pay ${money(total)} securely`}
          </button>
          <p className="text-xs text-[#86868B] text-center">By placing your order you agree to our <Link to="/terms-of-service" className="underline">Terms</Link> and <Link to="/privacy-policy" className="underline">Privacy Policy</Link>.</p>
        </form>

        <aside className="lg:col-span-2 lg:sticky lg:top-24 self-start bg-[#F5F5F7] rounded-3xl p-6 space-y-4" data-testid="checkout-summary">
          <p className="eyebrow">Order summary</p>
          <div className="space-y-3">
            {items.map((i) => {
              const key = `${i.product_id}::${i.variant_sku}`;
              return (
                <div key={key} className="flex gap-3 items-center">
                  {i.image && <img src={i.image} alt="" className="w-14 h-14 rounded-xl object-cover bg-white" />}
                  <div className="flex-1 text-sm">
                    <p className="font-medium">{i.product_name}</p>
                    <p className="text-xs text-[#86868B]">{i.variant_name} × {i.quantity}</p>
                  </div>
                  <p className="text-sm font-semibold">{money(i.unit_price * i.quantity)}</p>
                </div>
              );
            })}
          </div>
          <div className="border-t border-black/5 pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[#86868B]">Subtotal</span><span>{money(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-[#86868B]">Shipping</span><span>{shipping === 0 ? "Free" : money(shipping)}</span></div>
            {discount > 0 && <div className="flex justify-between text-[#34C759]"><span>Discount</span><span>-{money(discount)}</span></div>}
            <div className="flex justify-between font-display text-lg font-bold pt-2 border-t border-black/5"><span>Total</span><span data-testid="checkout-total">{money(total)}</span></div>
          </div>
        </aside>
      </section>
    </div>
  );
}
